import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { EconomicCalendarEvent } from "../../hooks/useEconomicCalendar";
import { trimEconomicEventName } from "./economicEventName";
import {
  displayCountry,
  eventTime,
  formatEconomicValue,
  impactColorOf,
  isFinitely,
} from "./economicEventFormat";
import { panelHairline } from "./overviewPanel";

// The phone/native economic event row. Same information as the desktop card,
// re-ranked for a column where vertical space is the scarce resource.
//
// TWO DELIBERATE DEPARTURES FROM EconomicEventRow (which the desktop band and
// the full-day modal keep unchanged):
//
// 1. The hierarchy INVERTS on state. A landed release leads with its actual —
//    that figure is the only thing on the row that is news. A pending release
//    has no such number yet, so the TIME leads instead: "when" is the only
//    real information that exists before the print. The row therefore always
//    has exactly one dominant element, and which one it is tells you the
//    state before you have read a word.
// 2. No card. Rows sit on hairlines instead of eight border edges (the design
//    system's own over-bordering anti-pattern), so the section reads as one
//    instrument — and stays visibly distinct from the bordered watchlist
//    cards below it: events are a list you read, companies are things you tap.
//
// Prev/Est are NOT ranked against the actual and never coloured. For half of
// these indicators a lower print is the good outcome (jobless claims), so a
// green/red surprise would be confidently wrong — the house rule is that a
// number is never presented as something it isn't.

const CondensedEconomicEventRow: React.FC<{
  event: EconomicCalendarEvent;
  /** Today's pending rows breathe until their print lands. */
  pulse: boolean;
  /** Hairline between rows; the last row of the list goes without. */
  divider: boolean;
}> = ({ event, pulse, divider }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const impactColor = impactColorOf(event);
  const country = displayCountry(event);

  const landed = isFinitely(event.actual);
  const time = eventTime(event);

  // The quiet qualifying line, joined by middots — country and time always,
  // then whichever of the reference figures exist. Once the actual has
  // landed the time demotes into this line and the figure takes the lead;
  // while pending, the time IS the lead and stays out of here.
  const qualifiers = [country, ...(landed ? [time] : [])];
  if (isFinitely(event.previous))
    qualifiers.push(`prev ${formatEconomicValue(event.previous, event.unit)}`);
  if (isFinitely(event.estimate))
    qualifiers.push(`est ${formatEconomicValue(event.estimate, event.unit)}`);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        py: 1,
        minWidth: 0,
        borderBottom: divider ? `1px solid ${panelHairline(isDark)}` : "none",
      }}
    >
      {/* Impact, as the row's only mark. Pending high-impact prints breathe;
          everything settled sits still. */}
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: impactColor,
          flexShrink: 0,
          ...(pulse &&
            !landed && {
              animation: "econDotPulse 2.2s ease-in-out infinite",
              "@keyframes econDotPulse": {
                "0%": { opacity: 1 },
                "50%": { opacity: 0.3 },
                "100%": { opacity: 1 },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }),
        }}
      />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {trimEconomicEventName(event.event)}
        </Typography>
        <Typography
          sx={{
            mt: 0.125,
            fontSize: "0.625rem",
            color: theme.palette.text.secondary,
            lineHeight: 1.4,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {qualifiers.join(" · ")}
        </Typography>
      </Box>

      {/* The lead: the print once it exists, the clock until then. Both sit
          in the same slot at the same size, so a row settling from pending to
          landed swaps its meaning without moving the layout. */}
      {landed && (
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            color: theme.palette.text.primary,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {formatEconomicValue(event.actual, event.unit)}
        </Typography>
      )}
      {!landed && (
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: theme.palette.text.secondary,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {time}
        </Typography>
      )}
    </Box>
  );
};

export default CondensedEconomicEventRow;
